/**
 * @fileoverview WebAuthn Unlock Dialog - Biometric unlock prompt for accessing encrypted data
 *
 * This component provides a dialog for unlocking encrypted data using:
 * 1. WebAuthn biometric (primary method)
 * 2. Password fallback
 * 3. Recovery key (24-word phrase)
 *
 * @module components/WebAuthnUnlockDialog
 */

import { useState } from 'react';
import { useWebAuthnUnlock } from '../hooks/useWebAuthnUnlock';
import { useEncryptionStatus } from '../hooks/useEncryptionStatus';
import { Fingerprint, Lock, Key, X, AlertCircle } from 'lucide-react';
import { Button } from '@xanectech/ui/components';

interface WebAuthnUnlockDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  onUnlockSuccess?: () => void;
  onSetupRequired?: () => void | Promise<void>;
  allowDismiss?: boolean;
}

type UnlockMethod = 'webauthn' | 'password' | 'recovery';

export function WebAuthnUnlockDialog({
  isOpen,
  onClose,
  onUnlockSuccess,
  onSetupRequired,
  allowDismiss = true,
}: WebAuthnUnlockDialogProps) {
  const [unlockMethod, setUnlockMethod] = useState<UnlockMethod>('webauthn');
  const [password, setPassword] = useState('');
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const {
    unlock,
    unlockWithPassword,
    unlockWithRecoveryKey,
    isUnlocking,
    error,
    progress,
    clearError,
  } = useWebAuthnUnlock();

  const { markUnlocked } = useEncryptionStatus();

  // Handle WebAuthn unlock
  const handleWebAuthnUnlock = async () => {
    clearError();
    const result = await unlock();

    // Check if setup is required
    if (!result.success && result.error?.includes('No primary devices')) {
      if (onSetupRequired) {
        const callbackResult = onSetupRequired();
        if (callbackResult instanceof Promise) {
          await callbackResult;
        }
      }
      return;
    }

    if (result.success) {
      await markUnlocked();
      onUnlockSuccess?.();
    }
  };

  // Handle password unlock
  const handlePasswordUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!password.trim()) {
      return;
    }

    const result = await unlockWithPassword(password);

    if (result.success) {
      await markUnlocked();
      setPassword('');
      onUnlockSuccess?.();
    }
  };

  // Handle recovery key unlock
  const handleRecoveryKeyUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    const words = recoveryPhrase.trim().split(/\s+/);
    if (words.length !== 24) {
      return;
    }

    const result = await unlockWithRecoveryKey(recoveryPhrase);

    if (result.success) {
      await markUnlocked();
      setRecoveryPhrase('');
      onUnlockSuccess?.();
    }
  };

  // Close handler
  const handleClose = () => {
    if (allowDismiss && onClose) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
      <div className='bg-card rounded-lg shadow-xl max-w-md w-full mx-4'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-border'>
          <h2 className='text-xl font-bold text-foreground'>
            🔐 Unlock Your Data
          </h2>
          {allowDismiss && (
            <Button
              variant='default'
              onClick={handleClose}
              className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              aria-label='Close'
            >
              <X className='w-5 h-5' />
            </Button>
          )}
        </div>

        {/* Content */}
        <div className='p-6 space-y-6'>
          {/* Method selector */}
          <div className='flex gap-2 bg-muted p-1 rounded-lg'>
            <Button
              variant={unlockMethod === 'webauthn' ? 'default' : 'ghost'}
              onClick={() => setUnlockMethod('webauthn')}
              className='flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors'
            >
              <Fingerprint className='w-4 h-4' />
              Biometric
            </Button>
            <Button
              variant={unlockMethod === 'password' ? 'default' : 'ghost'}
              onClick={() => setUnlockMethod('password')}
              className='flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors'
            >
              <Lock className='w-4 h-4' />
              Password
            </Button>
            <Button
              variant={unlockMethod === 'recovery' ? 'default' : 'ghost'}
              onClick={() => setUnlockMethod('recovery')}
              className='flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors'
            >
              <Key className='w-4 h-4' />
              Recovery
            </Button>
          </div>

          {/* Error display */}
          {error && (
            <div className='bg-destructive/10 dark:bg-destructive/5 border border-destructive/30 rounded-lg p-4'>
              <div className='flex gap-3'>
                <AlertCircle className='w-5 h-5 text-destructive flex-shrink-0' />
                <div>
                  <h4 className='text-sm font-medium text-red-800 dark:text-red-300'>
                    Unlock Failed
                  </h4>
                  <p className='text-sm text-red-700 dark:text-red-400 mt-1'>
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* WebAuthn unlock */}
          {unlockMethod === 'webauthn' && (
            <div className='space-y-4'>
              <p className='text-sm text-muted-foreground'>
                Use your biometric authentication (Touch ID, Face ID, or Windows
                Hello) to unlock your encrypted data.
              </p>

              {progress && (
                <div className='text-center py-4'>
                  <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600'></div>
                  <p className='mt-2 text-sm text-muted-foreground'>
                    {progress}
                  </p>
                </div>
              )}

              <Button
                variant='default'
                onClick={handleWebAuthnUnlock}
                disabled={isUnlocking}
                className='w-full flex items-center justify-center gap-2 py-3 px-4  disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors'
              >
                <Fingerprint className='w-5 h-5' />
                {isUnlocking ? 'Unlocking...' : 'Unlock with Biometric'}
              </Button>
            </div>
          )}

          {/* Password unlock */}
          {unlockMethod === 'password' && (
            <form onSubmit={handlePasswordUnlock} className='space-y-4'>
              <div>
                <label
                  htmlFor='password'
                  className='block text-sm font-medium text-foreground/90 mb-2'
                >
                  Password
                </label>
                <div className='relative'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id='password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder='Enter your password'
                    className='w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-background text-foreground'
                    disabled={isUnlocking}
                    autoComplete='current-password'
                  />
                  <Button
                    variant='default'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 '
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </Button>
                </div>
              </div>

              <Button
                type='submit'
                variant='default'
                disabled={isUnlocking || !password.trim()}
                className='w-full flex items-center justify-center gap-2 py-3 px-4 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors'
              >
                <Lock className='w-5 h-5' />
                {isUnlocking ? 'Unlocking...' : 'Unlock with Password'}
              </Button>
            </form>
          )}

          {/* Recovery key unlock */}
          {unlockMethod === 'recovery' && (
            <form onSubmit={handleRecoveryKeyUnlock} className='space-y-4'>
              <div>
                <label
                  htmlFor='recovery'
                  className='block text-sm font-medium text-foreground/90 mb-2'
                >
                  Recovery Key (24 words)
                </label>
                <textarea
                  id='recovery'
                  value={recoveryPhrase}
                  onChange={(e) => setRecoveryPhrase(e.target.value)}
                  placeholder='word1 word2 word3 ... word24'
                  rows={4}
                  className='w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-background text-foreground font-mono text-sm'
                  disabled={isUnlocking}
                />
                <p className='mt-1 text-xs text-muted-foreground'>
                  {recoveryPhrase.trim().split(/\s+/).filter(Boolean).length} /
                  24 words
                </p>
              </div>

              <Button
                type='submit'
                variant='default'
                disabled={
                  isUnlocking ||
                  recoveryPhrase.trim().split(/\s+/).filter(Boolean).length !==
                    24
                }
                className='w-full flex items-center justify-center gap-2 py-3 px-4 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors'
              >
                <Key className='w-5 h-5' />
                {isUnlocking ? 'Unlocking...' : 'Unlock with Recovery Key'}
              </Button>
            </form>
          )}

          {/* Help text */}
          <div className='text-center text-sm text-muted-foreground'>
            {unlockMethod === 'webauthn' && (
              <p>
                Can't use biometric?{' '}
                <Button
                  variant='link'
                  onClick={() => setUnlockMethod('password')}
                  className='text-indigo-600 hover:text-indigo-700 dark:text-indigo-400'
                >
                  Try password
                </Button>{' '}
                or{' '}
                <Button
                  variant='link'
                  onClick={() => setUnlockMethod('recovery')}
                  className='text-indigo-600 hover:text-indigo-700 dark:text-indigo-400'
                >
                  recovery key
                </Button>
              </p>
            )}
            {unlockMethod === 'password' && (
              <p>
                Forgot password?{' '}
                <Button
                  variant='link'
                  onClick={() => setUnlockMethod('recovery')}
                  className='text-indigo-600 hover:text-indigo-700 dark:text-indigo-400'
                >
                  Use recovery key
                </Button>
              </p>
            )}
            {unlockMethod === 'recovery' && (
              <p>
                Lost recovery key?{' '}
                <a
                  href='/support'
                  className='text-indigo-600 hover:text-indigo-700 dark:text-indigo-400'
                >
                  Contact support
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
